import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { useFilters } from '../context/FilterContext';
import type { LinkageContribution, Variable } from '../types';
import { AREA_ROUTES } from '../types';

export function AreaExplorerPage() {
  const { areaId = 'exports' } = useParams();
  const [searchParams] = useSearchParams();
  const { filters, setPolicyArea, setVariable } = useFilters();
  const [variables, setVariables] = useState<Variable[]>([]);
  const [contributions, setContributions] = useState<Record<string, LinkageContribution>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [traceId, setTraceId] = useState<string | null>(null);

  const areaName = AREA_ROUTES[areaId] || areaId;
  const highlightId = searchParams.get('variable') || filters.variableId;

  useEffect(() => {
    setPolicyArea(areaId);
  }, [areaId, setPolicyArea]);

  useEffect(() => {
    if (highlightId) {
      setTraceId(highlightId);
      setVariable(highlightId);
    }
  }, [highlightId, setVariable]);

  useEffect(() => {
    api.areaVariables(areaId).then(setVariables).catch(console.error);
    api.decomposition().then((d) => {
      const map: Record<string, LinkageContribution> = {};
      d.linkage_contributions.forEach((c) => { map[c.variable_id] = c; });
      setContributions(map);
    }).catch(console.error);
  }, [areaId]);

  const groups = useMemo(() => {
    const g: Record<string, Variable[]> = {};
    variables.forEach((v) => {
      const key = v.hierarchy_group || 'Other';
      (g[key] ||= []).push(v);
    });
    return g;
  }, [variables]);

  // A group is open unless explicitly set to false (default open).
  const isOpen = (g: string) => expanded[g] !== false;
  const toggle = (g: string) => setExpanded((e) => ({ ...e, [g]: e[g] === false }));

  return (
    <div>
      <h1 className="page-title">{areaName}</h1>
      <p className="page-subtitle">Policy-area explorer with collapsible indicator hierarchy.</p>

      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}>
        <button className="btn secondary" type="button" onClick={() => setExpanded(Object.fromEntries(Object.keys(groups).map((k) => [k, true])))}>Expand All</button>
        <button className="btn secondary" type="button" onClick={() => setExpanded(Object.fromEntries(Object.keys(groups).map((k) => [k, false])))}>Collapse All</button>
      </div>

      {Object.entries(groups).map(([group, vars]) => (
        <div key={group} className="card" style={{ padding: 0 }}>
          <div className="indicator-header" style={{ padding: '0.75rem 1rem' }} onClick={() => toggle(group)}>
            <strong>{isOpen(group) ? '▼' : '▶'} {group}</strong>
            <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>{vars.length} indicators</span>
          </div>
          {isOpen(group) && (
            <div style={{ padding: '0 1rem 1rem' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Indicator</th><th>Role</th><th>Scenario</th><th>Status</th><th>Contribution</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {vars.map((v) => {
                    const c = contributions[v.id];
                    const highlighted = highlightId === v.id;
                    return (
                      <tr key={v.id} className={highlighted ? 'row-highlight' : undefined} id={highlighted ? `var-${v.id}` : undefined}>
                        <td>{v.name}</td>
                        <td>{v.model_role || '—'}</td>
                        <td>{v.assumptions.accelerated ?? v.assumptions.incremental ?? '—'}</td>
                        <td>{v.data_status || '—'}</td>
                        <td>{c ? `${c.contribution_pp.toFixed(4)} pp` : '—'}</td>
                        <td>
                          {c && (
                            <button className="btn secondary" type="button" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }} onClick={() => setTraceId(traceId === v.id ? null : v.id)}>
                              Why?
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {vars.map((v) => traceId === v.id && contributions[v.id] && (
                <div key={`trace-${v.id}`} className="trace-panel">
                  <strong>Calculation — {v.id}</strong>
                  {contributions[v.id].trace?.map((t) => (
                    <div key={t.label}><span>{t.label}</span><span>{String(t.value)}{t.unit ? ` ${t.unit}` : ''}</span></div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
